import React from 'react';
import { OmniIconComponent } from './omni-components';
import '../../styles/InputChips.css';

class InputChips extends React.Component {
  constructor() {
    super();
    this.state = {
      chips: []
    };
    this.inputRef = React.createRef();
  }

  /**
   * Description `componentDidMount` is a lifecycle method in React that is called after a component is mounted
   * (i.e., inserted into the DOM). In this code, it checks if the `defaultValue` prop is provided and
   * if the component has not already been mounted. If both conditions are true, it sets the `mounted`
   * property to `true` and updates the state with the `defaultValue` prop. This allows the component
   * to be initialized with default values if they are provided.
   */
  componentDidMount = () => {
    if(this.props.defaultValue && !this.mounted){
      this.mounted = true;
      this.setState({chips:this.props.defaultValue});
    }
  }

  /**
   * Adds a value to the chips and performs validation before updating the state.
   *
   * @param {string} value - The value to be added to the chips.
   * @param {function} callback - A callback function to be executed after the state is updated.
   * @returns {void}
   */
  addValueToChips = (value, callback) => {
    let valid = true;
    if (this.props.validateInput) {
      valid = this.props.validateInput(value);
    }
    if (valid) {
      const updatedChips = [...this.state.chips, value];
      this.setState(
        {
          chips: updatedChips
        },
        () => {
          this.props.onChange(updatedChips);
          callback();
        }
      );
    }
  };

  /**
   * Updates the state with the input value added to the chips.
   *
   * @param {Event} event - The input change event.
   * @returns {void}
   */
  handleInputChange = (event) => {
    this.setState({
      chips: [...this.state.chips, event.target.value]
    });
  };

  /**
   * Handles the key press event and adds the input value to the chips if the Enter key is pressed and there is a value in the input field.
   *
   * @param {Event} event - The key press event.
   * @returns {void}
   */
  handleKeyPress = (event) => {
    if (event.key === 'Enter' && this.inputRef.current.value) {
      this.addValueToChips(this.inputRef.current.value, () => {
        this.inputRef.current.value = '';
      });
    }
  };

  /**
   * Removes a chip at the specified index from the chips and updates the state.
   *
   * @param {number} chipIndex - The index of the chip to be removed.
   * @returns {void}
   */
  handleChipRemove = (chipIndex) => {
    const updatedChips = [...this.state.chips];
    updatedChips.splice(chipIndex, 1);
    this.setState(
      {
        chips: updatedChips
      },
      () => {
        this.props.onChange(updatedChips);
      }
    );
  };

  /**
 * Event handler for the blur event on the input field.
 * Adds the input value to the chips and clears the input field.
 * @function
 * @name onBlurHandler
 * @returns {void}
 */
  onBlurHandler = () => {
    if (this.inputRef.current.value) {
      this.addValueToChips(this.inputRef.current.value, () => {
        this.inputRef.current.value = '';
      });
    }
  }
  render() {
    const { props } = this;
    return (
      <div className={`${props.containerClassName} custom-chips-input`}>
        {this.state.chips.map((chip, index) => (
          <span key={'chip' + index} className='chip'>
            {chip}
            <OmniIconComponent
              className='is-size-3 remove-icon'
              icon-id='omni:interactive:close'
              onClick={() => this.handleChipRemove(index)}
            />
          </span>
        ))}
        <input
          ref={this.inputRef}
          id={props.id}
          onKeyDown={this.handleKeyPress}
          value={props.value}
          placeholder={props.placeholder}
          onBlur={this.onBlurHandler}
        />
      </div>
    );
  }
}

export default InputChips;
